import clsx from "clsx";
import type { ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faCircleCheck,
  faSparkles,
  faUser,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import {
  CaseListGradeBadge,
  CaseReviewHeadlineGrade,
  CASE_CHECKING_REVIEW_GRADE_BADGE,
  ICON_CHECK_PASSED,
  type CaseCheckingGrade,
} from "@/components/case-checking/caseCheckingStatusBadges";
import { CC_INSET, CC_SUCCESS_INSET, CC_SUCCESS_INSET_BODY, CC_SUCCESS_INSET_TITLE } from "@/components/case-checking/caseCheckingChrome";
import {
  pillLabelForRating,
  RATING_TILE_CLASS,
} from "@/components/case-checking/problemStatementUi";
import type { ProblemStatementRating } from "@/services/api/case-checking";
import {
  RULE_CALL_LABEL,
  type PsRuleChangeQuote,
} from "@/components/case-checking/humanRuleReviewCascade";
import type { RuleIssueStatus } from "@/components/case-checking/problemStatementRuleIssues";

const RULE_STATUS_LABEL: Record<RuleIssueStatus, string> = {
  passed: "Pass",
  failed: "Fail",
  unclear: "Unclear",
  na: "N/A",
};

function CompareColumnHeader({
  icon,
  label,
  tone,
}: {
  icon: typeof faSparkles;
  label: string;
  tone: "agent" | "human";
}) {
  return (
    <p
      className={clsx(
        "mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide",
        tone === "agent" ? "text-slate-600 dark:text-slate-400" : "text-indigo-600 dark:text-indigo-300",
      )}
    >
      <FontAwesomeIcon icon={icon} className="h-3 w-3" />
      {label}
    </p>
  );
}

/** Overall case grade — agent vs human review. */
export function AgentHumanGradeCompare({
  agentGrade,
  humanGrade,
  ruleRollupGrade,
  className,
}: {
  agentGrade: CaseCheckingGrade;
  humanGrade: CaseCheckingGrade;
  ruleRollupGrade?: CaseCheckingGrade | null;
  className?: string;
}) {
  const humanDiffers = humanGrade !== agentGrade;
  const ruleDiffers = ruleRollupGrade != null && ruleRollupGrade !== agentGrade;

  if (!humanDiffers && !ruleDiffers) {
    return (
      <div className={className} data-testid="grade-compare-agreed">
        <CaseReviewHeadlineGrade grade={humanGrade} />
      </div>
    );
  }

  return (
    <div
      className={clsx("grid gap-3 sm:grid-cols-2", className)}
      data-testid="grade-compare-split"
    >
      <div className={CC_INSET}>
        <CompareColumnHeader icon={faSparkles} label="Agent" tone="agent" />
        <CaseListGradeBadge grade={agentGrade} className="!text-base" />
        {ruleDiffers && ruleRollupGrade ? (
          <p className="mt-2 text-xs text-slate-500">
            Rule rollup after your edits:{" "}
            <span className="font-medium text-slate-600 dark:text-slate-300">
              {CASE_CHECKING_REVIEW_GRADE_BADGE[ruleRollupGrade]?.label ?? ruleRollupGrade}
            </span>
          </p>
        ) : null}
      </div>
      <div className="rounded-lg bg-indigo-500/[0.06] p-3 ring-1 ring-indigo-500/20">
        <CompareColumnHeader icon={faUser} label="Your review" tone="human" />
        <CaseListGradeBadge grade={humanGrade} className="!text-base" />
      </div>
    </div>
  );
}

/** Problem-statement rating — agent vs effective after human edits. */
export function AgentHumanRatingCompare({
  agentRating,
  effectiveRating,
  compact = false,
  className,
}: {
  agentRating: ProblemStatementRating;
  effectiveRating: ProblemStatementRating;
  compact?: boolean;
  className?: string;
}) {
  if (agentRating === effectiveRating) {
    return (
      <span
        className={clsx(
          "inline-flex rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
          RATING_TILE_CLASS[agentRating],
          className,
        )}
      >
        {pillLabelForRating(agentRating)}
      </span>
    );
  }

  if (compact) {
    return (
      <span className={clsx("inline-flex flex-wrap items-center gap-1.5 text-[11px]", className)} data-testid="ps-rating-compare">
        <span
          className={clsx(
            "rounded-md px-1.5 py-0.5 ring-1 ring-inset text-slate-400 ring-slate-200 dark:ring-white/10",
          )}
        >
          Agent {pillLabelForRating(agentRating)}
        </span>
        <FontAwesomeIcon icon={faArrowRight} className="h-2.5 w-2.5 text-slate-600" />
        <span
          className={clsx(
            "rounded-md px-1.5 py-0.5 ring-1 ring-inset",
            RATING_TILE_CLASS[effectiveRating],
          )}
        >
          {pillLabelForRating(effectiveRating)}
        </span>
      </span>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="ps-rating-compare">
      <span className="text-[11px] text-slate-500">Agent</span>
      <span
        className={clsx(
          "rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset opacity-80",
          RATING_TILE_CLASS[agentRating],
        )}
      >
        {pillLabelForRating(agentRating)}
      </span>
      <FontAwesomeIcon icon={faArrowRight} className="h-3 w-3 text-slate-600" />
      <span className="text-[11px] text-indigo-600 dark:text-indigo-300">Your review</span>
      <span
        className={clsx(
          "rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
          RATING_TILE_CLASS[effectiveRating],
        )}
      >
        {pillLabelForRating(effectiveRating)}
      </span>
    </div>
  );
}

/** Rule verdict — agent vs human-effective status. */
export function AgentHumanRuleCompare({
  agentStatus,
  effectiveStatus,
}: {
  agentStatus: RuleIssueStatus;
  effectiveStatus: RuleIssueStatus;
}) {
  if (agentStatus === effectiveStatus) {
    return (
      <span className="text-[11px] font-medium text-slate-400">{RULE_STATUS_LABEL[effectiveStatus]}</span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-[11px]" data-testid="rule-verdict-compare">
      <span className="text-slate-500">Agent {RULE_STATUS_LABEL[agentStatus]}</span>
      <FontAwesomeIcon icon={faArrowRight} className="h-2.5 w-2.5 text-slate-600" />
      <span className="font-medium text-indigo-800 dark:text-indigo-200">{RULE_STATUS_LABEL[effectiveStatus]}</span>
    </span>
  );
}

const NARRATIVE_COLUMN_AGENT = CC_INSET;
const NARRATIVE_COLUMN_HUMAN =
  "rounded-lg bg-indigo-500/[0.06] p-3 ring-1 ring-indigo-500/20";

function NarrativeBlock({ children }: { children: ReactNode }) {
  return <div className="space-y-3 text-sm leading-relaxed">{children}</div>;
}

function AgentNarrativeBody({
  summary,
  gradeNote,
}: {
  summary?: string | null;
  gradeNote?: string | null;
}) {
  const hasContent = summary || gradeNote;
  if (!hasContent) {
    return <p className="text-sm text-slate-500">No agent summary yet.</p>;
  }
  return (
    <NarrativeBlock>
      {summary ? <p className="text-slate-600 dark:text-slate-300">{summary}</p> : null}
      {gradeNote ? <p className="text-slate-400">{gradeNote}</p> : null}
    </NarrativeBlock>
  );
}

/** Agent vs human narrative — split when a human overall review exists. */
export function AgentHumanNarrativeSplit({
  agentSummary,
  agentGradeNote,
  humanNotes,
  humanGrade,
  agentGrade,
  hasHumanReview,
  className,
}: {
  agentSummary?: string | null;
  agentGradeNote?: string | null;
  humanNotes?: string | null;
  humanGrade: CaseCheckingGrade;
  agentGrade: CaseCheckingGrade;
  hasHumanReview: boolean;
  className?: string;
}) {
  const humanDiffers = humanGrade !== agentGrade;
  const humanNotesBody = humanNotes?.trim() ?? "";

  if (!hasHumanReview) {
    return (
      <div className={className} data-testid="narrative-agent-only">
        <AgentNarrativeBody
          summary={agentSummary}
          gradeNote={agentGradeNote}
        />
      </div>
    );
  }

  return (
    <div className={clsx("grid gap-3 sm:grid-cols-2", className)} data-testid="narrative-compare-split">
      <div className={NARRATIVE_COLUMN_AGENT}>
        <CompareColumnHeader icon={faSparkles} label="Agent" tone="agent" />
        <AgentNarrativeBody
          summary={agentSummary}
          gradeNote={agentGradeNote}
        />
      </div>
      <div className={NARRATIVE_COLUMN_HUMAN}>
        <CompareColumnHeader icon={faUser} label="Your review" tone="human" />
        {humanDiffers ? (
          humanNotesBody ? (
            <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">{humanNotesBody}</p>
          ) : (
            <p className="text-sm italic text-slate-500">No override notes provided.</p>
          )
        ) : (
          <div className={clsx("flex items-start gap-2", CC_SUCCESS_INSET, "px-3 py-2.5")}>
            <FontAwesomeIcon icon={faCircleCheck} className={clsx("mt-0.5 h-4 w-4 shrink-0", ICON_CHECK_PASSED)} />
            <div>
              <p className={CC_SUCCESS_INSET_TITLE}>Agreed with agent</p>
              <p className={clsx("mt-0.5 text-xs", CC_SUCCESS_INSET_BODY)}>
                Your overall grade matches the agent assessment.
              </p>
              {humanNotesBody ? (
                <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{humanNotesBody}</p>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function HumanOverrideChip({ label = "Edited" }: { label?: string }) {
  return (
    <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-800 dark:text-indigo-200 ring-1 ring-inset ring-indigo-500/30 bg-indigo-500/10">
      {label}
    </span>
  );
}

/** Problem-statement row in Case Summary — agent narrative first, then human changes. */
export function PsStatementSummaryBody({
  agentRating,
  effectiveRating,
  skimText,
  ruleChanges,
  humanNotes,
  showStatementNow,
  statementNowLabel,
  isOverridden,
}: {
  agentRating: ProblemStatementRating;
  effectiveRating: ProblemStatementRating;
  skimText?: string | null;
  ruleChanges: PsRuleChangeQuote[];
  humanNotes?: string | null;
  showStatementNow?: boolean;
  statementNowLabel?: string;
  isOverridden: boolean;
}) {
  if (!isOverridden) {
    return (
      <div className="mt-1.5 flex min-w-0 items-center gap-2" data-testid="ps-summary-body">
        <AgentHumanRatingCompare
          agentRating={agentRating}
          effectiveRating={effectiveRating}
          compact
          className="shrink-0"
        />
        {skimText ? (
          <p className="min-w-0 flex-1 text-xs leading-relaxed text-slate-400 line-clamp-2 italic">{skimText}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="mt-1.5 space-y-2" data-testid="ps-summary-override">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Agent</p>
        <div className="mt-1 flex min-w-0 items-center gap-2">
          <span className="inline-flex shrink-0">
            <span
              className={clsx(
                "rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset",
                RATING_TILE_CLASS[agentRating],
              )}
            >
              {pillLabelForRating(agentRating)}
            </span>
          </span>
          {skimText ? (
            <p className="min-w-0 flex-1 text-xs leading-relaxed text-slate-400 line-clamp-2 italic">{skimText}</p>
          ) : null}
        </div>
      </div>
      <div className="rounded-md bg-indigo-500/[0.04] px-2.5 py-2 ring-1 ring-indigo-500/15">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-300">Your review</p>
        <span className="mt-1 block">
          <AgentHumanRatingCompare
            agentRating={agentRating}
            effectiveRating={effectiveRating}
            compact
          />
        </span>
        {ruleChanges.length > 0 ? <PsRuleChangeLines changes={ruleChanges} /> : null}
        {humanNotes?.trim() ? (
          <p className="mt-1.5 text-xs leading-relaxed text-slate-600 dark:text-slate-300 line-clamp-2 italic">
            {humanNotes.trim()}
          </p>
        ) : null}
        {showStatementNow && statementNowLabel ? (
          <p className="mt-1 text-[11px] text-slate-500">{statementNowLabel}</p>
        ) : null}
      </div>
    </div>
  );
}

/** Compact rule-level changes for summary lists (matches deepdive wording). */
export function PsRuleChangeLines({
  changes,
  maxVisible = 2,
}: {
  changes: PsRuleChangeQuote[];
  maxVisible?: number;
}) {
  if (changes.length === 0) return null;

  const priority = [...changes].sort((a, b) => Number(b.verdictChanged) - Number(a.verdictChanged));
  const visible = priority.slice(0, maxVisible);
  const hidden = priority.length - visible.length;

  return (
    <ul className="mt-1.5 space-y-1" data-testid="ps-rule-change-lines">
      {visible.map((change) => (
        <li
          key={change.ruleId}
          className="border-l-2 border-indigo-500/25 pl-2 text-[11px] leading-snug"
        >
          <span className="text-slate-400">{change.label}</span>
          <span className="text-indigo-800 dark:text-indigo-200/90">
            {" · "}
            {change.verdictChanged
              ? `${RULE_CALL_LABEL[change.humanCall]} (agent: ${RULE_CALL_LABEL[change.agentCall]})`
              : RULE_CALL_LABEL[change.humanCall]}
          </span>
        </li>
      ))}
      {hidden > 0 ? (
        <li className="pl-2 text-[11px] text-slate-500">
          +{hidden} more rule change{hidden === 1 ? "" : "s"}
        </li>
      ) : null}
    </ul>
  );
}

export function displayReviewerName(
  reviewerName?: string | null,
  displayAdviser?: string,
): string | null {
  const name = reviewerName?.trim();
  if (!name) return displayAdviser?.trim() || null;
  if (/^[a-f0-9]{24}$/i.test(name)) return displayAdviser?.trim() || null;
  return name;
}
